<template>
  <div :class="[selectedSkill && selectedSkill.skill.color + '-container']">
    <div class="grid w-full grid-cols-1 gap-8 md:grid-cols-2 md:gap-4">
      <div class="flex w-full flex-col gap-8 md:gap-4">
        <div v-if="selectedSkillId == null" class="flex flex-col gap-0.5">
          <div
            v-if="skillVisualitionOptions.length > 1"
            class="bg-surface-container-low rounded-t-extra-large rounded-b-small flex w-full flex-row justify-end px-4 py-2"
            :class="[
              chartView == SkillVisualization.Histogram &&
                groupedSkillsByParent.groupedSkills.length > 0 &&
                'rounded-b-extra-large! mb-4',
            ]"
          >
            <BaseConnectedButtonGroup
              v-model="chartView"
              size="XS"
              type="icon"
              color="outlined"
              :items="skillVisualitionOptions"
            />
          </div>
          <div
            v-if="
              chartView == SkillVisualization.MenonChart ||
              chartView == SkillVisualization.MenonChartSplit
            "
            class="bg-surface-container-low rounded-b-extra-large w-full p-4"
            :class="[
              skillVisualitionOptions.length > 1 && 'rounded-t-small',
              skillVisualitionOptions.length == 1 && 'rounded-t-extra-large',
            ]"
          >
            <MenonChart
              :max-value="scale.items.length"
              :skill-analysis="allSkills"
              :topic-perspectives="topicPerspectives"
              :split-by-perspective="chartView == SkillVisualization.MenonChartSplit"
              :value-shade="70"
              @skill-selected="selectSkill"
            />
          </div>
          <div
            v-if="chartView == SkillVisualization.Histogram"
            class="flex w-full flex-col gap-0.5"
          >
            <div
              v-for="group in groupedSkillsByParent.groupedSkills"
              :key="group.parentSkill.id"
              class="mb-4"
            >
              <div class="mb-2">
                <h3 class="text-title-medium text-on-surface-variant">
                  {{ group.parentSkill.title }}
                </h3>
              </div>
              <div class="flex flex-col gap-0.5">
                <Histogram
                  v-for="skill in group.childSkills"
                  :scale="scale"
                  :data="skill"
                  :topic-perspectives="topicPerspectives"
                  class="bg-surface-container-low last:rounded-b-large first:rounded-t-large rounded-extra-small p-4"
                  @click="selectSkill(skill.skill.id)"
                />
              </div>
            </div>
            <Histogram
              v-for="skillData in groupedSkillsByParent.ungroupedSkills"
              :key="skillData.skill.id"
              :scale="scale"
              :data="skillData"
              :topic-perspectives="topicPerspectives"
              class="bg-surface-container-low last:rounded-b-extra-large rounded-medium p-4"
              @click="selectSkill(skillData.skill.id)"
            />
          </div>
        </div>
        <SkillContainer
          v-else
          :skill="selectedSkill.skill"
          :subject="subject"
          :main-value="dataForSelectedSkill.mainValue ?? 0"
          :perspectives="dataForSelectedSkill.perspectives"
          :topic-perspectives="topicPerspectives"
          :scale="scale"
          @skil-unselected="unselectSkill"
        />
        <div v-if="texts.length > 0 && selectedSkillId == null">
          <TextFeedbackList
            :texts="texts"
            :expanded-question-id="expandedQuestionId"
            @question-clicked="questionClicked"
          />
        </div>
      </div>
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-0.5">
          <QuestionList
            :subject="subject"
            :questions="filteredQuestions"
            :topic-perspectives="topicPerspectives"
            :scale="scale"
            :expanded-question-id="expandedQuestionId"
            :show-blindspots="showBlindspots"
            @question-clicked="questionClicked"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<script lang="ts" setup>
  import { computed, ref, toRef } from 'vue'
  import MenonChart from './menon-chart/MenonChart.vue'
  import Histogram from './histogram/Histogram.vue'
  import TextFeedbackList from './text-feedback/TextFeedbackList.vue'
  import SkillContainer from './skill-container/SkillContainer.vue'
  import type {
    QuestionAnalysis,
    SkillAnalysis,
    TextAnalysis,
  } from '@/core/models/surveys-analytics'
  import type { Scale } from '@/core/models/scale'
  import { useSkillAnalysis } from '@/core/composables/use-skill-analysis'
  import type { MemberOrUser } from '@/core/models/member/member-or-user'
  import BaseConnectedButtonGroup from '../base/button/BaseConnectedButtonGroup.vue'
  import { SkillVisualization } from '@/core/models/feedback-topic'
  import type { Skill } from '@/core/models/skill.ts'
  import type { Perspective } from '@/core/models/perspective'
  import QuestionList from './question-list/QuestionList.vue'

  interface Props {
    scale: Scale
    subject: MemberOrUser
    questions: ReadonlyArray<QuestionAnalysis>
    skills: ReadonlyArray<SkillAnalysis>
    texts: ReadonlyArray<TextAnalysis>
    topicPerspectives: ReadonlyArray<Perspective>
    skillVisualization: ReadonlyArray<SkillVisualization>
    showBlindspots: boolean
    hideEmptySkills?: boolean
  }

  const props = defineProps<Props>()

  const selectedSkill = computed(() => {
    const skill = allSkills.value.find((skill) => skill.skill.id == selectedSkillId.value)
    return skill!
  })

  const selectSkill = (skillId: string) => {
    selectedSkillId.value = skillId
  }

  const unselectSkill = () => {
    selectedSkillId.value = null
  }

  const chartView = ref<SkillVisualization>(props.skillVisualization[0])
  const selectedSkillId = ref<string | null>(null)
  const expandedQuestionId = ref<string | null>(null)

  const questionClicked = (questionId: string) => {
    if (expandedQuestionId.value == questionId) {
      expandedQuestionId.value = null
    } else {
      expandedQuestionId.value = questionId
    }
  }

  const dataForSelectedSkill = computed(() => {
    const data = allSkills.value.find((d) => d.skill.id == selectedSkillId.value)!
    return {
      mainValue: data.average,
      perspectives: data.perspectives,
    }
  })

  const filteredQuestions = computed(() => {
    if (selectedSkillId.value) {
      return props.questions.filter((question) =>
        (question.question.skillIds ?? []).includes(selectedSkillId.value!),
      )
    }

    return props.questions
  })
  const { allSkills } = useSkillAnalysis(toRef(props, 'skills'))

  const skillVisualitionOptions = computed(() => {
    const options = []
    for (const visualization of props.skillVisualization) {
      if (visualization === SkillVisualization.MenonChart) {
        options.push({
          label: 'Menon Chart',
          value: SkillVisualization.MenonChart,
          icon: 'pie_chart',
        })
      } else if (visualization === SkillVisualization.MenonChartSplit) {
        options.push({
          label: 'Menon Chart',
          value: SkillVisualization.MenonChartSplit,
          icon: 'pie_chart',
        })
      } else if (visualization === SkillVisualization.Histogram) {
        options.push({ label: 'Histogram', value: SkillVisualization.Histogram, icon: 'bar_chart' })
      }
    }

    return options
  })

  const groupedSkillsByParent = computed(() => {
    const groupedSkills: Map<string, { parentSkill: Skill; childSkills: SkillAnalysis[] }> =
      new Map()
    const ungroupedSkills: SkillAnalysis[] = []

    for (const skill of props.skills) {
      if (props.hideEmptySkills && skill.perspectives.every((p) => p.average == null)) {
        continue
      }
      if (skill.skill.parentSkill) {
        groupedSkills.set(skill.skill.parentSkill.id, {
          parentSkill: skill.skill.parentSkill,
          childSkills: [
            ...(groupedSkills.get(skill.skill.parentSkill.id)?.childSkills || []),
            skill,
          ],
        })
      } else {
        ungroupedSkills.push(skill)
      }
    }
    return { groupedSkills: Array.from(groupedSkills.values()), ungroupedSkills }
  })
</script>
